import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchJson } from "../lib/api";
import { apiBase } from "../lib/apiBase";
import { safeArray } from "../utils/api";

export const INTENSITY_ORDER = ["LOW", "MEDIUM", "HIGH"];
const WHALE_SUMMARY_STALE_MS = 5 * 60 * 1000;
const DEFAULT_ON_UNAUTHORIZED = () => {};

function isValidTimestamp(value) {
    if (!value) return false;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed);
}

function isStaleTimestamp(value) {
    if (!isValidTimestamp(value)) return true;
    return Date.now() - Date.parse(value) > WHALE_SUMMARY_STALE_MS;
}

function latestValidTimestamp(values) {
    let latest = null;
    values.forEach((value) => {
        if (!isValidTimestamp(value)) return;
        const parsed = Date.parse(value);
        if (latest === null || parsed > latest) latest = parsed;
    });
    return latest === null ? "" : new Date(latest).toISOString();
}

function whaleEventTimestamp(payload, alerts, biases) {
    const summary = payload?.summary && typeof payload.summary === "object" ? payload.summary : {};
    if (isValidTimestamp(summary.last_alert_ts)) return summary.last_alert_ts;

    const events = safeArray(payload?.events);
    const eventTimestamp = latestValidTimestamp([
        ...alerts.map((alert) => alert?.timestamp),
        ...events.map((event) => event?.timestamp || event?.ts || event?.last_ts),
        ...biases.map((bias) => bias?.last_updated),
    ]);
    if (eventTimestamp) return eventTimestamp;

    const totalEvents = Number(summary.total_events ?? alerts.length ?? 0);
    const hasEventRows = alerts.length > 0 || events.length > 0 || biases.length > 0 || totalEvents > 0;
    return hasEventRows ? null : "";
}

function intensityRank(value = "") {
    const idx = INTENSITY_ORDER.indexOf(String(value || "").toUpperCase());
    return idx === -1 ? 0 : idx;
}

export default function useWhaleSummary({ authenticated = true, pollIntervalMs = 20000, onUnauthorized = DEFAULT_ON_UNAUTHORIZED } = {}) {
    const [alerts, setAlerts] = useState([]);
    const [biases, setBiases] = useState([]);
    const [lastUpdated, setLastUpdated] = useState("");
    const [influenceEnabled, setInfluenceEnabled] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!authenticated) {
            setAlerts([]);
            setBiases([]);
            setLoading(false);
            setError("unauthorized");
            return;
        }
        try {
            const res = await fetchJson(apiBase("whale_alerts"));
            if (res?.unauth) {
                setError("unauthorized");
                setAlerts([]);
                setBiases([]);
                onUnauthorized();
                return;
            }
            if (res?.ok === false) {
                setError(res?.status || "error");
                setAlerts([]);
                setBiases([]);
                return;
            }
            const payload = res?.data || {};
            if (
                !payload ||
                typeof payload !== "object" ||
                payload.ok === false ||
                payload.status === false ||
                !Array.isArray(payload.alerts) ||
                !Array.isArray(payload.biases) ||
                !isValidTimestamp(payload.timestamp)
            ) {
                setError("invalid_payload");
                setAlerts([]);
                setBiases([]);
                setLastUpdated("");
                return;
            }
            const safeAlerts = safeArray(payload.alerts);
            const safeBiases = safeArray(payload.biases);
            const eventTimestamp = whaleEventTimestamp(payload, safeAlerts, safeBiases);
            setAlerts(safeAlerts);
            setBiases(safeBiases);
            setLastUpdated(payload.timestamp || "");
            setInfluenceEnabled(payload.influence_enabled === true);
            if (eventTimestamp === null) {
                setError("stale");
            } else {
                setError(eventTimestamp && isStaleTimestamp(eventTimestamp) ? "stale" : null);
            }
        } catch {
            setError("unavailable");
            setAlerts([]);
            setBiases([]);
        } finally {
            setLoading(false);
        }
    }, [authenticated, onUnauthorized]);

    useEffect(() => {
        load();
        if (!authenticated) return undefined;
        const id = window.setInterval(load, pollIntervalMs);
        return () => window.clearInterval(id);
    }, [authenticated, load, pollIntervalMs]);

    const maxBiasLevel = useMemo(() => {
        if (!biases.length) return "none";
        const maxIdx = Math.max(...biases.map((b) => intensityRank(b.intensity)));
        return (INTENSITY_ORDER[maxIdx] || "none").toLowerCase();
    }, [biases]);

    const hasPressure = useMemo(() => influenceEnabled && biases.length > 0, [biases.length, influenceEnabled]);

    return {
        alerts,
        biases,
        lastUpdated,
        influenceEnabled,
        loading,
        error,
        hasPressure,
        maxBiasLevel,
        recentAlertCount: alerts.length,
    };
}
